// Lightweight wrapper around face-api.js (TinyFaceDetector) used by the
// points-watching camera to decide whether the captured frame actually
// contains a clear face. Runs entirely in the browser — no image leaves the
// device for this check.
//
// Model files must live in `public/models/` (served at `/models`):
//   - tiny_face_detector_model-weights_manifest.json
//   - tiny_face_detector_model-shard1
// Grab them from the face-api.js repo (weights/ folder). Until they're present
// detection is a safe no-op: detectFace() resolves to null and the server
// falls back to its previous "photo present = OK" behavior (no false alerts).

import * as faceapi from 'face-api.js';

const MODEL_URL = '/models';

// A face counts as "proper" only when the detector is reasonably confident
// AND the face is big enough relative to the frame — this rejects tiny
// background faces / noise. Tunable.
const SCORE_THRESHOLD = 0.5;
const MIN_FACE_WIDTH_RATIO = 0.12; // face box width >= 12% of the image width
// Gameroom cameras shoot under heavy colored (purple/blue) lighting, which
// wrecks the skin tones TinyFaceDetector was trained on — real faces score
// near zero on the raw frame. Converting to grayscale first neutralizes the
// cast and restores detection (verified: 0 -> 0.83 on a tinted capture). A
// larger input size than the 320 default also helps recall.
const INPUT_SIZE = 416;

let modelsPromise = null;
let modelsReady = false;

// Loads the TinyFaceDetector model once. Resolves true on success, false if
// the model files can't be loaded (missing/offline) so callers can degrade
// gracefully instead of throwing.
export function loadFaceModels() {
  if (modelsReady) return Promise.resolve(true);
  if (!modelsPromise) {
    modelsPromise = faceapi.nets.tinyFaceDetector
      .loadFromUri(MODEL_URL)
      .then(() => {
        modelsReady = true;
        return true;
      })
      .catch((err) => {
        // Reset so a later attempt (e.g. after files are deployed) can retry.
        modelsPromise = null;
        console.error('face-api.js models failed to load from', MODEL_URL, err);
        return false;
      });
  }
  return modelsPromise;
}

// Detects whether `input` (an HTMLCanvasElement / HTMLVideoElement / HTMLImageElement)
// contains a clear face.
//   returns true  -> a proper face was found
//   returns false -> no proper face
//   returns null  -> detection couldn't run (models missing or error); caller
//                    should treat this as "unknown" and not raise a false alert
export async function detectFace(input) {
  try {
    const ok = await loadFaceModels();
    if (!ok) return null;

    // Grayscale the frame first to neutralize the colored gameroom lighting.
    const gray = toGrayscaleCanvas(input);
    const target = gray || input;

    const options = new faceapi.TinyFaceDetectorOptions({
      inputSize: INPUT_SIZE,
      scoreThreshold: SCORE_THRESHOLD,
    });
    const detections = await faceapi.detectAllFaces(target, options);
    if (!detections || detections.length === 0) return false;

    const imgWidth =
      input.width || input.videoWidth || input.naturalWidth || 640;
    const minWidth = imgWidth * MIN_FACE_WIDTH_RATIO;

    return detections.some(
      (d) => (d.box && d.box.width >= minWidth) && d.score >= SCORE_THRESHOLD
    );
  } catch (err) {
    console.error('face detection error', err);
    return null;
  }
}

// Returns a new canvas holding the grayscale version of `input`
// (HTMLCanvasElement / HTMLVideoElement / HTMLImageElement). Returns null if a
// canvas can't be created so the caller falls back to the raw input.
function toGrayscaleCanvas(input) {
  try {
    const w = input.width || input.videoWidth || input.naturalWidth;
    const h = input.height || input.videoHeight || input.naturalHeight;
    if (!w || !h) return null;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(input, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    const px = img.data;
    for (let i = 0; i < px.length; i += 4) {
      const l = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      px[i] = px[i + 1] = px[i + 2] = l;
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
  } catch (err) {
    return null;
  }
}
